/**
 * Multi-tenant wizard session storage.
 *
 * Format (localStorage key "onboarding_wizard_sessions_v2"):
 *   {
 *     [companyId: string]: {
 *       companyId: string,
 *       currentStep: number,
 *       provider: string | null,
 *       step1Data: object | null,
 *       step2Data: object | null,
 *       gwsSuperAdminEmail: string | null,   // persisted when admin mailbox created at Step 3
 *     }
 *   }
 *
 * A session entry is created when the wizard first advances past step 1 and
 * is cleared only when the wizard reaches completion (Done step).
 * Cancelling ("Cancel & leave") preserves the entry so the user can resume.
 */

const STORAGE_KEY = 'onboarding_wizard_sessions_v2'
const LEGACY_KEY = 'onboarding_wizard_session_v1'

// One-shot migration: promote a v1 flat session to the v2 map on first read.
function migrateSessions() {
  try {
    const raw = window.localStorage.getItem(LEGACY_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw)
    if (!parsed?.companyId) return

    const existing = getSessions()
    if (!existing[parsed.companyId]) {
      existing[parsed.companyId] = {
        companyId: parsed.companyId,
        currentStep: parsed.currentStep ?? 3,
        provider: parsed.provider ?? null,
        step1Data: parsed.step1Data ?? null,
        step2Data: parsed.step2Data ?? null,
      }
      writeSessions(existing)
    }
    window.localStorage.removeItem(LEGACY_KEY)
  } catch {
    // ignore
  }
}

function writeSessions(sessions) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions))
  } catch {
    // localStorage unavailable — nothing the user can do
  }
}

export function getSessions() {
  migrateSessions()
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}') || {}
  } catch {
    return {}
  }
}

export function getSession(companyId) {
  return getSessions()[String(companyId)] ?? null
}

export function setSession(companyId, data) {
  const sessions = getSessions()
  sessions[String(companyId)] = { companyId: String(companyId), ...data }
  writeSessions(sessions)
}

export function clearSession(companyId) {
  const sessions = getSessions()
  delete sessions[String(companyId)]
  writeSessions(sessions)
}

export function hasSession(companyId) {
  return getSession(companyId) !== null
}

export function getGwsSuperAdminEmail(companyId) {
  return getSession(companyId)?.gwsSuperAdminEmail ?? null
}

export function getCompanyName(companyId) {
  return getSession(companyId)?.step1Data?.name ?? null
}

export function getOwnerInfo(companyId) {
  const step1 = getSession(companyId)?.step1Data
  if (!step1?.ownerEmail) return null
  return {
    email: step1.ownerEmail,
    firstName: step1.ownerFirstName || '',
    lastName: step1.ownerLastName || '',
  }
}

export function setGwsSuperAdminEmail(companyId, email) {
  const sessions = getSessions()
  const existing = sessions[String(companyId)] || { companyId: String(companyId) }
  sessions[String(companyId)] = { ...existing, gwsSuperAdminEmail: email }
  writeSessions(sessions)
}
